<template>
	<AppLayout :title="boardArea.name" :subtitle="trans('association.boards.areas.members.workgroup.title')">
		<Modal id="create-workgroup-member-modal">
			<template #title>{{ trans('admin.users.boards.areas.members.create.title') }}</template>
			<template #subtitle>{{ boardArea.name }}</template>
			<LabeledObject v-if="selectedUser" required full>
				<template #label>{{ trans('general.associate') }}</template>
				<List>
					<ListItem :title="selectedUser.full_name">
						<SoftButton :color="Color.Red" @click.stop="deselectUser">
							<i class="fa-solid fa-trash mr-2"/>
						</SoftButton>
					</ListItem>
				</List>
			</LabeledObject>
			<SearchModel v-else attribute="full_name" :label="trans('general.associate')" required relative search-type="users" @selected="selectUser"/>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-workgroup-member-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Empty v-if="members.length === 0"/>
		<CardGrid v-else>
			<Card v-for="member in members" :key="`workgroup-member-${member.id}`" :title="member.user.full_name">
				<SanctionableDetails :sanctionable="member"/>
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('association.boards.areas.workgroup-members.edit', {board: board.id, boardArea: boardArea.id, boardWorkgroupMember: member.id})">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import {type Rank} from '@/Types/Association/Rank'
import Modal from '@/Components/UI/Modals/Modal.vue'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import {type Board} from '@/Types/Association/Boards/Board'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SearchModel from '@/Components/Search/SearchModel.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type BoardArea} from '@/Types/Association/Boards/BoardArea'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type BoardWorkgroupMember} from '@/Types/Association/Boards/BoardWorkgroupMember'
import SanctionableDetails from '@/Components/Association/Sanctions/SanctionableDetails.vue'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	board:Board,
	ranks:Array<Rank>,
	boardArea:BoardArea,
	members:Array<BoardWorkgroupMember>,
}>()

const board:Ref<Board> = ref(props.board)
const working:Ref<boolean> = ref(false)
const selectedUser:Ref<User|null> = ref(null)
const boardArea:Ref<BoardArea> = ref(props.boardArea)
const members:Ref<Array<BoardWorkgroupMember>> = ref(props.members)

function selectUser(user:User):void{
	selectedUser.value = user
}

function deselectUser():void{
	selectedUser.value = null
}

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.boards.areas.workgroup-members.store', {board: board.value.id, boardArea: boardArea.value.id}), {
			user_id: selectedUser.value.id,
		}).then(({data}:{data:BoardWorkgroupMember}) => {
			showSuccess(trans('general.inserted'))
			members.value.unshift(data)
			closeModal('create-workgroup-member-modal')
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>